// /Users/mac/Documents/FG5.1/js/components/Shop.js

import { SHOP_PRODUCTS, CURRENCIES } from '../data/shopData.js';
import { getProductsPage } from '../productService.js';
import { filterAndSortProducts, highlightMatch } from '../utils/shopUtils.js';
import { db, auth } from '../../src/firebase.js';
import { collection, getDocs, query, limit } from 'firebase/firestore';

const SHOP_HIERARCHY = {
    'Sport & Training': ['Tops & T-Shirts', 'Sports Bras', 'Shorts', 'Leggings & Tights', 'Sweatshirts & Hoodies', 'Tracksuits', 'Jackets', 'Socks'],
    'Casual & Street': ['Oversized Tees', 'Joggers', 'Caps & Beanies', 'Tote Bags', 'Backpacks', 'Crossbody Bags'],
    'Home & Living': ['Mugs', 'Water Bottles', 'Towels', 'Pillowcases', 'Blankets & Throws', 'Posters & Art Prints', 'Candles'],
    'Tech & Accessories': ['Phone Cases', 'Notebooks & Stationery', 'Keyrings', 'Stickers & Badges', 'Scarves'],
    'Kids & Baby': ['Baby Bodysuits', 'Kids T-Shirts', 'Kids Hoodies']
};

export class Shop {
    constructor(containerId, initialCategory = 'All') {
        this.containerId = containerId;
        this.el = null;
        // Component State
        this.products = []; // Now populated via Firestore
        this.currentCurrency = CURRENCIES[0]; // Default to Euro
        this.isPriceCurrencySelectorOpen = false;
        this.activeFilters = {
            categories: initialCategory === 'All' ? [] : [initialCategory], // Now an array for multi-select
            searchQuery: '',
            priceRanges: [],
            size: [],
            collections: [],
            motion: []
        };
        this.lastScrollY = 0;
        this.renderTimeout = null;
        this.openParentCategory = null;
        if (initialCategory !== 'All') {
            for (const [parent, subs] of Object.entries(SHOP_HIERARCHY)) {
                if (subs.includes(initialCategory)) {
                    this.openParentCategory = parent;
                    break;
                }
            }
        }
        this.openFilterId = null; // Tracks which filter (Price, Size, etc.) is open
        this.applyFiltersTimeout = null;
        // Pagination State
        this.lastDoc = null;
        this.hasMore = true;
        this.isLoading = false;
        this.observer = null;
        this.pageSize = 12; // Define page size
        this.dynamicCollections = [];
        this.currentRequestId = 0;
    }

    init() {
        this.el = document.getElementById(this.containerId);
        if (!this.el) return;

        this.injectStyles();

        this.el.innerHTML = `
            <div id="fg-mobile-header-container">
                ${this.renderMobileHeader()}
            </div>
            <div class="fg-shop" id="fg-shop-wrapper">
                <aside class="fg-shop__sidebar" id="fg-shop-sidebar"></aside>
                <main class="fg-shop__main">
                    ${this.renderTopBar()}
                    ${this.renderProductGrid()}
                </main>
            </div>
            <div class="fg-filter-modal-overlay" id="fg-filter-overlay"></div>
            <div class="fg-filter-modal" id="fg-filter-modal">
                <div class="fg-filter-modal__header">
                    <h2 class="fg-filter-modal__title" id="fg-filter-modal-title"></h2>
                    <button class="fg-filter-modal__close" id="fg-filter-modal-close">✕</button>
                </div>
                <div class="fg-filter-modal__body" id="fg-filter-modal-body"></div>
                <div class="fg-filter-modal__footer">
                    <button class="fg-btn fg-btn--primary fg-filter-modal__apply" id="fg-filter-modal-apply">Apply</button>
                </div>
            </div>
            <div class="fg-sort-modal" id="fg-sort-modal">
                <div class="fg-sort-modal__header">
                    <h2 class="fg-sort-modal__title">Sort By</h2>
                    <button class="fg-filter-modal__close" id="fg-sort-modal-close">✕</button>
                </div>
                <div class="fg-sort-modal__body">
                    <button class="fg-sort-option" data-sort-value="featured">Featured</button>
                    <button class="fg-sort-option" data-sort-value="newest">Newest</button>
                    <button class="fg-sort-option" data-sort-value="price-asc">Price: Low to High</button>
                    <button class="fg-sort-option" data-sort-value="price-desc">Price: High to Low</button>
                </div>
            </div>
        `;

        this.rerenderSidebar();
        this.bindEvents();
        this.applyFiltersAndSort();
        this.syncNavigationUI();
    }

    injectStyles() {
        const styleId = 'fg-shop-custom-styles';
        if (document.getElementById(styleId)) return;

        const css = `
            .fg-product-card__image-container {
                position: relative;
                width: 100%;
                aspect-ratio: 1 / 1;
                overflow: hidden;
                border-radius: 12px;
                background-color: var(--bg-secondary);
            }
            .fg-product-card__image-track {
                display: flex;
                height: 100%;
                overflow-x: auto;
                scroll-snap-type: x mandatory;
                scrollbar-width: none;
                -ms-overflow-style: none;
                cursor: grab;
            }
            .fg-product-card__image-track::-webkit-scrollbar { display: none; }
            .fg-product-card__image-track:active { cursor: grabbing; }
            .fg-product-card__img-slide {
                flex: 0 0 100%;
                height: 100%;
                scroll-snap-align: start;
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
            }
            .fg-product-card__image-dots {
                position: absolute;
                bottom: 8px;
                left: 50%;
                transform: translateX(-50%);
                display: flex;
                gap: 4px;
                pointer-events: none;
                z-index: 5;
            }
            .fg-image-dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(255,255,255,0.4); transition: background 0.2s, transform 0.2s; }
            .fg-image-dot.active { background: #fff; transform: scale(1.2); }
        `;
        const style = document.createElement('style'); style.id = styleId; style.textContent = css; document.head.appendChild(style);
    }

    async fetchDynamicCollections() {
        try {
            const q = query(collection(db, 'products'), limit(50));
            const querySnapshot = await getDocs(q);
            const collectionsSet = new Set();
            querySnapshot.forEach(doc => {
                const data = doc.data();
                if (data.collections && Array.isArray(data.collections)) {
                    data.collections.forEach(c => collectionsSet.add(c));
                }
            });
            this.dynamicCollections = Array.from(collectionsSet).sort();
        } catch (error) {
            console.error("[Shop] Error fetching dynamic collections:", error);
            this.dynamicCollections = [];
        }
    }

    renderMobileHeader() {
        const categoryNav = this.renderCategoryHierarchy();
        const chips = ['Collections', 'Size', 'Shop By Price', 'Motion'];
    
        return `
            <div class="fg-shop-mobile-header">
                <div class="fg-shop-mobile-header__title">
                    <h1>
                        <span class="fg-brand-football">football</span><span class="fg-brand-girl">girl</span> <span class="fg-shop-title-shop">Shop</span>
                    </h1>
                </div>
                <div class="fg-shop-search-bar fg-shop-search-bar--mobile">
                    <svg class="fg-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                    <input type="text" class="fg-search-input" placeholder="Search products..." value="${this.activeFilters.searchQuery}">
                    <button class="fg-search-clear" style="display: ${this.activeFilters.searchQuery ? 'flex' : 'none'}" aria-label="Clear search">✕</button>
                </div>
                <div class="fg-category-tabs">
                    ${categoryNav}
                </div>
                <div class="fg-filter-chips">
                    <button class="fg-filter-chips__btn fg-filter-chips__btn--sort" data-sort-trigger>
                        Sort By
                    </button>
                    ${chips.map(chip => `<button class="fg-filter-chips__btn" data-filter="${chip}">${chip}</button>`).join('')}
                </div>
            </div>
        `;
    }

    renderCategoryHierarchy() {
        const { categories: activeCategories } = this.activeFilters;
        const allActive = activeCategories.length === 0 ? 'is-active' : '';

        let html = `
            <div class="fg-cat-all-wrap">
                <button class="fg-cat-group__label ${allActive}" data-slug="All">
                    All Products
                </button>
            </div>
        `;

        for (const [parent, subs] of Object.entries(SHOP_HIERARCHY)) {
            const isOpen = this.openParentCategory === parent;
            const isGroupActive = activeCategories.includes(parent) || subs.some(sub => activeCategories.includes(sub));
            
            html += `
                <div class="fg-cat-group ${isOpen ? 'is-open' : ''} ${isGroupActive ? 'is-active' : ''}">
                    <div class="fg-cat-group__header" data-parent-toggle="${parent}">
                        <span class="fg-cat-group__label">${parent}</span>
                        <span class="fg-cat-group__arrow">
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </span>
                    </div>
                    <div class="fg-cat-group__sub">
                        <div class="fg-cat-group__sub-inner">
                            ${subs.map(sub => {
                                const isActive = activeCategories.includes(sub) ? 'is-active' : '';
                                return `
                                    <button class="fg-cat-group__sub-item ${isActive}" data-subcategory="${sub}">
                                        ${sub}
                                    </button>
                                `;
                            }).join('')}
                        </div>
                    </div>
                </div>`;
        }
        return html;
    }

    renderSidebar() {
        const categoryNav = this.renderCategoryHierarchy();
        const filterSections = [
            { id: 'price', title: `Shop By Price` },
            { id: 'size', title: 'Size' },
            { id: 'motion', title: 'Motion' },
            { id: 'collections', title: 'Collections' }
        ];

        const accordions = filterSections.map(section => {
            const isOpen = this.openFilterId === section.id;
            let content = `<p>Options for ${section.title}...</p>`; // Default placeholder

            if (section.id === 'price') {
                const currencyOptions = CURRENCIES.map(c => `<li data-code="${c.code}">${c.symbol} ${c.code} — ${c.name}</li>`).join('');
                // Dynamically generate price ranges with current currency symbol
                const priceRanges = [0, 20, 40, 60, 80, 100].map((min, i, arr) => {
                    const max = arr[i + 1];
                    return max ? `${this.currentCurrency.symbol}${min}–${this.currentCurrency.symbol}${max}` : `${this.currentCurrency.symbol}${min}+`;
                });
                const priceCheckboxes = priceRanges.map((range, i) => `
                    <div class="fg-cat-group__sub-item fg-checkbox-row">
                        <input type="checkbox" id="price-${i}" data-value="${range}" ${this.activeFilters.priceRanges.includes(range) ? 'checked' : ''}>
                        <label for="price-${i}">${range}</label>
                    </div>
                `).join('');

                content = `
                    <div class="fg-currency-selector-wrapper">
                        <button class="fg-currency-selector__toggle" data-currency-toggle>
                            <span>${this.currentCurrency.symbol} ${this.currentCurrency.code} — ${this.currentCurrency.name}</span>
                            <span class="fg-cat-group__arrow ${this.isPriceCurrencySelectorOpen ? 'is-open' : ''}">›</span>
                        </button>
                        <ul class="fg-currency-selector" ${!this.isPriceCurrencySelectorOpen ? 'style="display: none;"' : ''}>
                            ${currencyOptions}
                        </ul>
                    </div>
                    <div class="fg-price-ranges">
                        ${priceCheckboxes}
                    </div>
                `;
            } else if (section.id === 'size') {
                const sizes = ['S', 'M', 'L', 'XL', '2XL'];
                content = sizes.map((size, i) => `
                     <div class="fg-cat-group__sub-item fg-checkbox-row ${this.activeFilters.size.includes(size) ? 'is-active' : ''}">
                        <input type="checkbox" id="size-${i}" data-value="${size}" ${this.activeFilters.size.includes(size) ? 'checked' : ''}>
                        <label for="size-${i}">${size}</label>
                    </div>
                `).join('');
            } else if (section.id === 'motion') {
                const motionOptions = ['Lifestyle', 'Running', 'Training & Gym', 'Football', 'Home'];
                content = motionOptions.map((opt, i) => `
                    <div class="fg-cat-group__sub-item fg-checkbox-row ${this.activeFilters.motion.includes(opt) ? 'is-active' : ''}">
                        <input type="checkbox" id="motion-${i}" data-value="${opt}" ${this.activeFilters.motion.includes(opt) ? 'checked' : ''}>
                        <label for="motion-${i}">${opt}</label>
                    </div>
                `).join('');
            } else if (section.id === 'collections') {
                const collections = ['S/S 2026', 'A/W 2026'];
                content = collections.map((coll, i) => `
                     <div class="fg-cat-group__sub-item fg-checkbox-row ${this.activeFilters.collections.includes(coll) ? 'is-active' : ''}">
                        <input type="checkbox" id="coll-${i}" data-value="${coll}" ${this.activeFilters.collections.includes(coll) ? 'checked' : ''}>
                        <label for="coll-${i}">${coll}</label>
                    </div>
                `).join('');
            }

            return `
            <div class="fg-cat-group ${isOpen ? 'is-open' : ''}" data-filter-id="${section.id}">
                <div class="fg-cat-group__header">
                    <button class="fg-cat-group__label" data-filter-toggle="${section.id}">${section.title}</button>
                    <button class="fg-cat-group__arrow" data-filter-toggle="${section.id}">›</button>
                </div>
                <div class="fg-cat-group__sub">
                    <div class="fg-cat-group__sub-inner">
                        ${content}
                    </div>
                </div>
            </div>
        `}).join('');

        return `
            <div class="fg-shop-categories">
                ${categoryNav}
            </div>
            ${accordions}
        `;
    }

    renderTopBar() {
        return `
            <div class="fg-shop-topbar">
                <h1 class="fg-shop-topbar__title">
                    <span class="fg-brand-football">football</span><span class="fg-brand-girl">girl</span> <span class="fg-shop-title-shop">Shop</span>
                </h1>
                <div class="fg-shop-topbar__actions">
                    <div class="fg-shop-search-bar">
                        <svg class="fg-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                        <input type="text" class="fg-search-input" placeholder="Search..." value="${this.activeFilters.searchQuery}">
                        <button class="fg-search-clear" style="display: ${this.activeFilters.searchQuery ? 'flex' : 'none'}" aria-label="Clear search">✕</button>
                    </div>
                    <button class="fg-btn" id="fg-hide-filters-btn">Hide Filters</button>
                    <select class="fg-select" id="fg-sort-select">
                        <option value="featured">Sort By: Featured</option>
                        <option value="newest">Newest</option>
                        <option value="price-desc">Price: High-Low</option>
                        <option value="price-asc">Price: Low-High</option>
                    </select>
                </div>
            </div>
        `;
    }

    renderProductGrid() {
        return `
            <div id="fg-active-chips-container">${this.renderActiveFilterChips()}</div>
            <div class="fg-product-grid fg-shop-product-grid" id="fg-product-grid-container"></div>
            <div id="fg-grid-footer" style="padding: 40px 0; text-align: center;">
                <div id="fg-scroll-sentinel" style="height: 20px;"></div>
                <div id="fg-grid-loader" class="fg-product-grid fg-shop-product-grid" style="display: none;">
                    ${this.renderSkeletonBatch()}
                </div>
                <p id="fg-all-loaded-msg" style="display: none; color: var(--text-secondary); font-size: 0.9rem;">You have seen everything</p>
            </div>
        `;
    }

    renderActiveFilterChips() {
        const chips = []; // Array to hold all active filter chip data
        const { categories, searchQuery, priceRanges, size, collections, motion } = this.activeFilters;

        // Add chips for selected subcategories
        categories.forEach(cat => {
            chips.push({ type: 'categories', label: cat, value: cat });
        });

        if (searchQuery) chips.push({ type: 'searchQuery', label: `"${searchQuery}"`, value: searchQuery });
        priceRanges.forEach(v => chips.push({ type: 'priceRanges', label: v, value: v }));
        size.forEach(v => chips.push({ type: 'size', label: v, value: v }));
        collections.forEach(v => chips.push({ type: 'collections', label: v, value: v }));
        motion.forEach(v => chips.push({ type: 'motion', label: v, value: v }));

        if (chips.length === 0) return '';

        const chipsHtml = chips.map(chip => `
            <div class="fg-active-chip">
                <span class="fg-active-chip__label">${chip.label}</span>
                <button class="fg-active-chip__remove" data-type="${chip.type}" data-value="${chip.value}" aria-label="Remove filter">✕</button>
            </div>
        `).join('');

        const clearAllHtml = chips.length > 1 ? `
            <button class="fg-active-chips__clear-all" id="fg-chips-clear-all">Clear all</button>
        ` : '';

        return `
            <div class="fg-active-chips">
                <div class="fg-active-chips__list">
                    ${chipsHtml}
                    ${clearAllHtml}
                </div>
            </div>
        `;
    }

    renderProductCard(p) {
        const colors = p.colors.map(color => `<span class="fg-product-card__color-dot" style="background-color: ${color};"></span>`).join('');
        const recycledLabel = p.isRecycled ? `<div class="fg-product-card__recycled-label">Recycled Materials</div>` : '';
        const isFullySoldOut = p.sizes && p.sizes.every(size => !p.stock || p.stock[size] === 0);
        const soldOutBadge = isFullySoldOut ? '<div class="fg-product-card__sold-out">Sold Out</div>' : '';
        const images = p.images || [];
        const imageHtml = images.length > 0 
            ? images.map(src => `<div class="fg-product-card__img-slide" style="background-image: url('${src}');"></div>`).join('')
            : `<div class="fg-product-card__img-slide" style="background-color: ${p.imagePlaceholder}"></div>`;

        return `
            <article class="fg-product-card" data-product-id="${p.id}">
                <div class="fg-product-card__image-container">
                    ${soldOutBadge}
                    <div class="fg-product-card__image-track">
                        ${imageHtml}
                    </div>
                    ${images.length > 1 ? `
                        <div class="fg-product-card__image-dots">
                            ${images.map((_, i) => `<span class="fg-image-dot ${i === 0 ? 'active' : ''}"></span>`).join('')}
                        </div>
                    ` : ''}
                </div>
                <div class="fg-product-card__body">
                    <div class="fg-product-card__colors">${colors}</div>
                    ${recycledLabel}
                    <h3 class="fg-product-card__name">${highlightMatch(p.name, this.activeFilters.searchQuery)}</h3>
                    <p class="fg-product-card__desc">${highlightMatch(p.subtitle, this.activeFilters.searchQuery)}</p>
                    <div class="fg-product-card__price">${p.price}</div>
                </div>
            </article>
        `;
    }

    renderPromoBanner() {
        return `
            <div class="fg-promo-banner">
                <div class="fg-promo-banner__image"></div>
                <div class="fg-promo-banner__content">
                    <h3>footballgirl Collections</h3>
                    <p>The new standard in performance and style.</p>
                    <button class="fg-btn fg-btn--white">Shop Now</button>
                </div>
            </div>
        `;
    }

    toggleAccordion(button) {
        const content = button.nextElementSibling;
        const icon = button.querySelector('.fg-shop-accordion__icon');
        const isOpen = content.classList.toggle('is-open');
        button.setAttribute('aria-expanded', isOpen);
        icon.textContent = isOpen ? '−' : '+';
    }

    getFilterOptions(filterName) {
        const map = {
            'Collections': ['S/S 2026', 'A/W 2026'],
            'Size': ['S', 'M', 'L', 'XL', '2XL'],
            'Shop By Price': ['0–20', '20–40', '40–60', '60–80', '80–100'],
            'Motion': ['Lifestyle', 'Running', 'Training & Gym', 'Football', 'Home']
        };
        return map[filterName] || [];
    }

    openFilterModal(filterName) {
        const modal = this.el.querySelector('#fg-filter-modal');
        const overlay = this.el.querySelector('#fg-filter-overlay');
        const title = this.el.querySelector('#fg-filter-modal-title');
        const body = this.el.querySelector('#fg-filter-modal-body');

        title.textContent = `Filter by ${filterName}`;
        const options = this.getFilterOptions(filterName);
        body.innerHTML = options.map((opt, i) => `
            <div class="fg-checkbox-row">
                <input type="checkbox" id="modal-filter-${i}" data-filter="${filterName}" data-value="${opt}">
                <label for="modal-filter-${i}">${opt}</label>
            </div>
        `).join('');

        modal.classList.add('is-open');
        overlay.classList.add('is-open');
    }

    closeFilterModal() {
        this.el.querySelector('#fg-filter-modal')?.classList.remove('is-open');
        this.el.querySelector('#fg-filter-overlay')?.classList.remove('is-open');
    }

    renderSkeletonBatch() {
        const count = window.innerWidth <= 768 ? 2 : 3;
        return Array(count).fill(this.renderSkeletonCard()).join('');
    }

    async applyFiltersAndSort() {
        if (this.applyFiltersTimeout) clearTimeout(this.applyFiltersTimeout);

        return new Promise((resolve) => {
            this.applyFiltersTimeout = setTimeout(async () => {
                this.lastDoc = null;
                this.hasMore = true;
                this.products = [];
                
                const container = this.el.querySelector('#fg-product-grid-container');
                const chipsContainer = this.el.querySelector('#fg-active-chips-container');
                if (container) container.innerHTML = this.renderSkeletonBatch(); // Show skeletons immediately
                if (chipsContainer) chipsContainer.innerHTML = this.renderActiveFilterChips(); // Update chips immediately

                await this.loadNextPage(true);
                this.setupInfiniteScroll();
                this.syncNavigationUI();
                resolve();
            }, 300);
        });
    }

    async loadNextPage(isReset = false) {
        // If it's a reset, we force the load even if isLoading is true
        if (!isReset && (this.isLoading || !this.hasMore)) return;
        
        this.isLoading = true;
        const requestId = ++this.currentRequestId;
        
        // Hide "You have seen everything" message when loading
        this.el.querySelector('#fg-all-loaded-msg').style.display = 'none';
        
        const loader = this.el.querySelector('#fg-grid-loader');
        if (loader) loader.style.display = 'grid';

        const { searchQuery, collections, size, categories } = this.activeFilters;
        const serviceFilters = { searchQuery };

        // Handle category logic: if single selection, use array-contains on 'collections'
        if (categories.length === 1) {
            serviceFilters.category = categories[0];
        } else if (categories.length > 1) {
            serviceFilters.categories = categories;
        }

        if (size.length > 0) serviceFilters.size = size[0];
        if (collections.length > 0) serviceFilters.collection = collections[0];

        // FIX 3: Price range filtering
        if (this.activeFilters.priceRanges.length > 0) {
            // Assuming only one price range can be active for Firestore query
            const range = this.activeFilters.priceRanges[0];
            const [minStr, maxStr] = range.split('–').map(s => s.replace(this.currentCurrency.symbol, ''));
            serviceFilters.minPrice = parseFloat(minStr);
            serviceFilters.maxPrice = maxStr.includes('+') ? undefined : parseFloat(maxStr);
        }

        const sortBy = this.el.querySelector('#fg-sort-select')?.value || 'featured';

        console.log('Attempting Firestore fetch...');
        try {
            const result = await getProductsPage(serviceFilters, sortBy, this.lastDoc, this.pageSize);
            if (requestId !== this.currentRequestId) return;
            console.log('Firestore success, products:', result.products);

            // Filter results locally for search query because Firestore doesn't support partial matching across fields
            let displayProducts = result.products;
            if (searchQuery) {
                const q = searchQuery.toLowerCase().trim();
                displayProducts = displayProducts.filter(p => 
                    p.name?.toLowerCase().includes(q) || 
                    p.title?.toLowerCase().includes(q) || 
                    p.subtitle?.toLowerCase().includes(q) ||
                    p.description?.toLowerCase().includes(q)
                );

                // Relevancy sorting: Names/Titles match higher than descriptions
                displayProducts.sort((a, b) => {
                    const aNameMatch = (a.name?.toLowerCase().includes(q) || a.title?.toLowerCase().includes(q));
                    const bNameMatch = (b.name?.toLowerCase().includes(q) || b.title?.toLowerCase().includes(q));
                    if (aNameMatch && !bNameMatch) return -1;
                    if (!aNameMatch && bNameMatch) return 1;
                    return 0;
                });
            }

            // Log unique metadata found in products to help verify UI mapping
            const uniqueCats = [...new Set(displayProducts.map(p => p.category))];
            const uniqueTags = [...new Set(displayProducts.flatMap(p => p.collections || []))];
            console.log('[Shop Service] Unique categories in this batch:', uniqueCats);
            console.log('[Shop Service] Unique collection/hierarchy tags:', uniqueTags);

            this.lastDoc = result.lastDoc;
            this.hasMore = result.hasMore;
            
            const container = this.el.querySelector('#fg-product-grid-container');
            if (isReset) container.innerHTML = '';
            
            if (displayProducts.length === 0 && isReset) {
                container.innerHTML = `<div class="fg-shop-empty-state">
                        <h2 class="fg-shop-empty-state__title">No products found</h2>
                        <button class="fg-btn" id="fg-retry-load-btn" style="margin-top: 1rem; width: auto; padding: 0.5rem 2rem;">Retry</button>
                    </div>`;
            } else {
                this.products.push(...displayProducts);
                container.insertAdjacentHTML('beforeend', displayProducts.map(p => this.renderProductCard(p)).join(''));
                this.bindProductCardEvents();
            }
        } catch (error) {
            if (requestId !== this.currentRequestId) return;
            
            console.error("[Shop] Data Fetch Error:", error);

            // If it's a permission error, inform the developer/user specifically
            if (error.code === 'permission-denied') {
                const container = this.el.querySelector('#fg-product-grid-container');
                if (isReset) {
                    container.innerHTML = `<div class="fg-shop-empty-state"><h2 style="color: #ef4444;">Access Denied</h2><p>Firestore security rules are blocking product access. Please check your rules in the Firebase console.</p></div>`;
                }
            }
            
            const sortBy = this.el.querySelector('#fg-sort-select')?.value || 'featured';
            const allLocalFiltered = filterAndSortProducts(SHOP_PRODUCTS, this.activeFilters, sortBy); // Use local filters
            
            const start = this.products.length;
            const batch = allLocalFiltered.slice(start, start + this.pageSize);
            
            const container = this.el.querySelector('#fg-product-grid-container');
            if (isReset) container.innerHTML = '';
            
            if (batch.length > 0) {
                container.insertAdjacentHTML('beforeend', batch.map(p => this.renderProductCard(p)).join(''));
                this.products.push(...batch);
                this.bindProductCardEvents();
            } else if (isReset && error.code !== 'permission-denied') {
                // Only show "No products" if we aren't already showing the "Access Denied" message
                const container = this.el.querySelector('#fg-product-grid-container');
                container.innerHTML = `<div class="fg-shop-empty-state"><h2>No products found</h2><p>We're having trouble reaching the store. Please try again later.</p></div>`;
            }
            this.hasMore = this.products.length < allLocalFiltered.length;
        } finally {
            if (requestId !== this.currentRequestId) return;
            this.isLoading = false;
            if (loader) loader.style.display = 'none';
            this.el.querySelector('#fg-all-loaded-msg').style.display = !this.hasMore ? 'block' : 'none';
        }
    }

    setupInfiniteScroll() {
        if (this.observer) this.observer.disconnect();
        const sentinel = this.el.querySelector('#fg-scroll-sentinel');
        if (!sentinel) return;

        this.observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && this.hasMore && !this.isLoading) {
                this.loadNextPage();
            }
        }, { rootMargin: '200px' });
        this.observer.observe(sentinel);
    }

    syncNavigationUI() {
        const mobileContainer = this.el.querySelector('#fg-mobile-header-container');
        if (mobileContainer) mobileContainer.innerHTML = this.renderMobileHeader();
        
        this.rerenderSidebar();
        this._scrollToActiveCategory();
    }

    /**
     * Automatically scrolls the horizontal category bar to the active pill on mobile.
     */
    _scrollToActiveCategory() {
        if (window.innerWidth > 768) return;
        
        const container = this.el.querySelector('.fg-category-tabs');
        if (!container) return;

        const activeEl = container.querySelector('.fg-cat-group.is-active, .fg-cat-all-wrap:has(.is-active)');
        
        if (activeEl) {
            activeEl.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }

    renderSkeletonCard() {
        return `
            <div class="fg-product-skeleton">
                <div class="fg-product-skeleton__image"></div>
                <div class="fg-product-skeleton__body">
                    <div class="fg-product-skeleton__line fg-product-skeleton__line--title"></div>
                    <div class="fg-product-skeleton__line fg-product-skeleton__line--subtitle"></div>
                    <div class="fg-product-skeleton__line fg-product-skeleton__line--price"></div>
                </div>
            </div>
        `;
    }

    rerenderGrid() {
        const chipsContainer = this.el.querySelector('#fg-active-chips-container');
        const gridContainer = this.el.querySelector('#fg-product-grid-container'); // This is where products are appended
        if (!chipsContainer || !gridContainer) return;

        // 1. Update chips immediately so the UI feels responsive
        chipsContainer.innerHTML = this.renderActiveFilterChips();

        // 2. Show Skeletons
        const isMobile = window.innerWidth <= 768;
        const skeletonCount = isMobile ? 4 : 6;
        gridContainer.innerHTML = Array(skeletonCount).fill(this.renderSkeletonCard()).join('');

        // 3. Clear previous pending render to handle rapid clicks
        if (this.renderTimeout) clearTimeout(this.renderTimeout);

        // 4. Transition to real products after minimum 400ms
        this.renderTimeout = setTimeout(() => {
            const gridContent = this.products.length === 0
                ? `<div class="fg-shop-empty-state">
                       <h2 class="fg-shop-empty-state__title">No products found</h2>
                       <p class="fg-shop-empty-state__subtitle">Try adjusting your filters</p>
                       <div style="display: flex; gap: 10px; margin-top: 1rem; justify-content: center;">
                           <button class="fg-btn" id="fg-clear-filters-btn" style="width: auto;">Clear all filters</button>
                           <button class="fg-btn" id="fg-retry-load-btn" style="width: auto;">Retry</button>
                       </div>
                   </div>`
                : `
                    ${this.products.map(p => this.renderProductCard(p)).join('')}
                    ${this.renderPromoBanner()}
                    <div class="fg-shop-banner-full">Image Coming Soon</div>
                    <div class="fg-shop-banner-full">Image Coming Soon</div>
                `;
            
            gridContainer.innerHTML = gridContent;
            this.bindProductCardEvents();

            // Re-bind clear button if empty state is rendered
            this.el.querySelector('#fg-clear-filters-btn')?.addEventListener('click', () => this.resetFilters());
        }, 400);
    }

    resetFilters() {
        this.activeFilters = {
            categories: [],
            searchQuery: '',
            priceRanges: [],
            size: [],
            collections: [],
            motion: []
        };
        this.openParentCategory = null;

        this.el.querySelectorAll('.fg-category-tabs__btn').forEach(btn => btn.classList.remove('is-active'));
        const sortSelect = this.el.querySelector('#fg-sort-select');
        if (sortSelect) sortSelect.value = 'featured';

        this.applyFiltersAndSort();
        this.rerenderSidebar();
    }

    rerenderSidebar() {
        const sidebarContainer = this.el.querySelector('#fg-shop-sidebar');
        if (!sidebarContainer) return;
        sidebarContainer.innerHTML = this.renderSidebar();
        this.el.querySelectorAll('.fg-price-ranges input[type="checkbox"]').forEach(checkbox => {
            const label = checkbox.nextElementSibling.textContent.trim();
            checkbox.checked = this.activeFilters.priceRanges.includes(label);
        });
        this.bindSidebarEvents();
    }

    bindEvents() {
        this.bindProductCardEvents();

        // Mobile: Hide sticky filter bar on scroll down, show on scroll up
        window.addEventListener('scroll', () => {
            if (window.innerWidth > 768) return;
            
            const stickyBar = this.el?.querySelector('.fg-shop-mobile-header__sticky-bar');
            if (!stickyBar) return;

            const currentScrollY = window.scrollY;
            
            // Hide if scrolling down and passed the initial header area
            if (currentScrollY > this.lastScrollY && currentScrollY > 100) {
                stickyBar.classList.add('is-hidden');
            } else {
                stickyBar.classList.remove('is-hidden');
            }
            this.lastScrollY = currentScrollY;
        }, { passive: true });

        // Unified Category Navigation (Tabs & Sidebar)
        this.el.addEventListener('click', e => {
            const labelBtn = e.target.closest('.fg-cat-group__label');
            const arrowBtn = e.target.closest('.fg-cat-group__arrow');
            const subBtn = e.target.closest('.fg-cat-group__sub-item');
            const sortTrigger = e.target.closest('[data-sort-trigger]');
            const filterChipTrigger = e.target.closest('.fg-filter-chips__btn[data-filter]');

            // Product Card Click (Delegated for infinite scroll)
            const productCard = e.target.closest('.fg-product-card');
            if (productCard) {
                window.dispatchEvent(new CustomEvent('fg:navigate', {
                    detail: { page: 'product-detail', productId: productCard.dataset.productId }
                }));
                return;
            }

            // Mobile Sort/Filter Triggers (Delegated for rerender safety)
            if (sortTrigger) {
                this.el.querySelector('#fg-sort-modal')?.classList.add('is-open');
                this.el.querySelector('#fg-filter-overlay')?.classList.add('is-open');
                return;
            }
            if (filterChipTrigger) {
                this.openFilterModal(filterChipTrigger.dataset.filter);
                return;
            }

            // Mobile Sort Option Click
            const sortOption = e.target.closest('.fg-sort-option');
            if (sortOption) {
                const sortSelect = this.el.querySelector('#fg-sort-select');
                if (sortSelect) sortSelect.value = sortOption.dataset.sortValue;
                this.applyFiltersAndSort();
                this.el.querySelector('#fg-sort-modal')?.classList.remove('is-open');
                this.el.querySelector('#fg-filter-overlay')?.classList.remove('is-open');
                return;
            }

            // Handle "All Products"
            const allBtn = e.target.closest('[data-slug="All"]');
            if (allBtn) {
                this.resetFilters();
                this.openParentCategory = null;
                this.syncNavigationUI();
                return;
            }

            // Handle Parent Category Toggle
            const parentToggle = e.target.closest('[data-parent-toggle]');
            if (parentToggle) {
                const parentName = parentToggle.dataset.parentToggle;
                const isClosing = this.openParentCategory === parentName;
                this.openParentCategory = isClosing ? null : parentName;
                
                if (window.innerWidth <= 768) {
                    // On mobile, clicking the pill should trigger the filter
                    this.activeFilters.categories = isClosing ? [] : [parentName];
                    this.applyFiltersAndSort();
                }

                this.syncNavigationUI();
                return;
            }

            // Handle Subcategory selection
            const subItem = e.target.closest('[data-subcategory]');
            if (subItem) {
                const sub = subItem.dataset.subcategory;
                this.activeFilters.categories = [sub];
                this.applyFiltersAndSort();
                this.syncNavigationUI();
                return;
            }

            // Handle Checkbox changes for Price, Size, Motion, Collections
            const checkbox = e.target.closest('.fg-checkbox-row input[type="checkbox"]');
            // Skip checkboxes inside the filter modal (they are handled by the Apply button)
            if (checkbox && !checkbox.id.startsWith('modal-filter')) {
                const filterType = checkbox.dataset.filterType || checkbox.id.split('-')[0];
                const value = checkbox.dataset.value;
                if (checkbox.checked) {
                    this.activeFilters[filterType].push(value);
                } else {
                    this.activeFilters[filterType] = this.activeFilters[filterType].filter(v => v !== value);
                }
                this.applyFiltersAndSort();
            }

            // Handle Filter Toggles (Price, Size, etc.)
            const filterToggle = e.target.closest('[data-filter-toggle]');
            if (filterToggle) {
                const filterId = filterToggle.dataset.filterToggle;
                this.openFilterId = this.openFilterId === filterId ? null : filterId;
                this.rerenderSidebar();
            }
        });

        // Search Input Handling (Delegation)
        this.el.addEventListener('input', e => {
            if (e.target.classList.contains('fg-search-input')) {
                const val = e.target.value;
                this.activeFilters.searchQuery = val;
                
                // Toggle clear button visibility immediately
                const clearBtn = e.target.parentElement.querySelector('.fg-search-clear');
                if (clearBtn) clearBtn.style.display = val ? 'flex' : 'none';

                this.applyFiltersAndSort();
            }
        });

        const sortSelect = this.el.querySelector('#fg-sort-select');
        sortSelect?.addEventListener('change', () => {
            this.applyFiltersAndSort();
        });

        const hideFiltersBtn = this.el.querySelector('#fg-hide-filters-btn');
        const shopWrapper = this.el.querySelector('#fg-shop-wrapper');
        if (hideFiltersBtn) {
            hideFiltersBtn.addEventListener('click', () => {
                const isHidden = shopWrapper.classList.toggle('fg-shop--no-sidebar');
                hideFiltersBtn.textContent = isHidden ? 'Show Filters' : 'Hide Filters';
            });
        }

        // Close modal
        this.el.querySelector('#fg-filter-modal-close')?.addEventListener('click', () => this.closeFilterModal());
        this.el.querySelector('#fg-filter-overlay')?.addEventListener('click', () => this.closeFilterModal());

        // Apply button
        this.el.querySelector('#fg-filter-modal-apply')?.addEventListener('click', () => {
            const filterName = this.el.querySelector('#fg-filter-modal-title')
                .textContent.replace('Filter by ', '').trim();
            
            const keyMap = {
                'Shop By Price': 'priceRanges',
                'Size': 'size',
                'Collections': 'collections',
                'Motion': 'motion'
            };
            
            const key = keyMap[filterName];
            if (key) {
                const checked = [...this.el.querySelectorAll('#fg-filter-modal-body input:checked')]
                    .map(cb => cb.dataset.value);
                this.activeFilters[key] = checked;
            }
            
            this.applyFiltersAndSort();
            this.closeFilterModal();
        });

        this.el.querySelector('#fg-sort-modal-close')?.addEventListener('click', () => {
            this.el.querySelector('#fg-sort-modal').classList.remove('is-open');
            this.el.querySelector('#fg-filter-overlay').classList.remove('is-open');
        });

        // Active Chip Listeners
        this.el.addEventListener('click', e => { // Moved search clear logic here to avoid conflict
            const clearBtn = e.target.closest('.fg-search-clear');
            if (clearBtn) {
                const input = clearBtn.parentElement.querySelector('.fg-search-input');
                if (input) {
                    input.value = '';
                    this.activeFilters.searchQuery = '';
                    clearBtn.style.display = 'none';
                    input.focus();
                    this.applyFiltersAndSort();
                }
            }
            if (e.target.classList.contains('fg-active-chip__remove')) {
                const { type, value } = e.target.dataset;
                if (type === 'categories') { // Handle multiple category chips
                    this.activeFilters.categories = this.activeFilters.categories.filter(cat => cat !== value);
                } else if (type === 'searchQuery') {
                    this.activeFilters.searchQuery = '';
                    this.syncNavigationUI(); // To clear the text box value
                } else {
                    this.activeFilters[type] = this.activeFilters[type].filter(v => v !== value);
                }
                this.applyFiltersAndSort();
            }
            if (e.target.id === 'fg-chips-clear-all') this.resetFilters();
            
            if (e.target.id === 'fg-retry-load-btn') {
                this.currentRequestId = 0;
                this.applyFiltersAndSort();
            }
        });

        // Clear all filters listener
        this.el.addEventListener('click', e => {
            if (e.target.id === 'fg-clear-filters-btn') {
                this.resetFilters();
            }
        });
    }

    bindProductCardEvents() {
        const tracks = this.el.querySelectorAll('.fg-product-card__image-track');
        tracks.forEach(track => {
            const dots = track.parentElement.querySelectorAll('.fg-image-dot');
            if (dots.length === 0) return;

            track.addEventListener('scroll', () => {
                const index = Math.round(track.scrollLeft / track.clientWidth);
                dots.forEach((dot, i) => {
                    dot.classList.toggle('active', i === index);
                });
            }, { passive: true });
        });
    }

    bindSidebarEvents() {
        // Currency dropdown toggle
        const currencyToggle = this.el.querySelector('[data-currency-toggle]');
        currencyToggle?.addEventListener('click', () => {
            this.isPriceCurrencySelectorOpen = !this.isPriceCurrencySelectorOpen;
            const dropdown = this.el.querySelector('.fg-currency-selector');
            if (dropdown) {
                dropdown.style.display = this.isPriceCurrencySelectorOpen ? 'block' : 'none';
            }
        });

        // Currency selection
        this.el.querySelectorAll('.fg-currency-selector li').forEach(li => {
            li.addEventListener('click', () => {
                const newCode = li.dataset.code;
                this.currentCurrency = CURRENCIES.find(c => c.code === newCode) || CURRENCIES[0];
                this.isPriceCurrencySelectorOpen = false; // Close dropdown
                this.rerenderSidebar(); // Re-render sidebar to update the title and close the dropdown
            });
        });

    }
}
